/* Cart v1.0 08/11/2023 */

import {writable, derived, get} from 'svelte/store';
import {closeOrderItem, updateOrderItemData} from "../core/screens-manager.js";
import {reachGoal} from "../core/ecommerce.js";
import {eventBus} from "../core/event-bus-store.js";
import {menuStore, publishConfigStore} from "../core/app-store.js";
import {getMenuId} from "../core/get-menu-id.js";

/**
 * {
 *  id,
 *  name,
 *  ref, - reference tenant (optional)
 *  img,
 *  price,
 *  weight,
 *  count
 * }
 *
 * @type {Writable<{ id, name, ref, img, price, weight, count}[]>}
 */
export const cartItemsStore = writable([]);

/**
 * {
 *  count: number,
 *  sum: number
 * }
 *
 * @type {Readable<{count: number, sum: number}>}
 */
export const cartTotalStore = derived(cartItemsStore, (items) => {

    const total = {
        count: 0,
        sum: 0
    };

    (items || []).forEach(item => {
        total.sum += ((item.price || 0) * item.count);
        total.count += item.count;
    });

    return total;
})

/**
 *
 * @type {Writable<*[]>}
 */
export const ordersStore = writable([]);

/**
 *
 * @type {Writable<{orderData: string}>}
 */
export const cartInfoStore = writable({orderData: null});


cartItemsStore.subscribe(cartItems => {
    if (cartItems && cartItems.length > 0) {
        saveCart(cartItems);
    }

    cartInfoStore.update(cartInfo => {
        cartInfo.orderData = undefined;
        return cartInfo;
    });
})

const getModsDescription = (mods) => (mods || []).map(m => m.map(x => x.name).join(' + ')).join(' + ');

const findItemPredicate = (item, record, priceIndex, modsDescription) => {

    return item.id === record.id &&
        item.priceIndex === priceIndex &&
        item.modsDescription === modsDescription;
}

export const addToCart = (record, price, priceIndex, mods = []) => {

    cartItemsStore.update(items => {

        const modsDescription = getModsDescription(mods);
        const found = (items || []).findIndex(i => findItemPredicate(i, record, priceIndex, modsDescription));

        if (found === -1) {
            items.push({
                menuKey: get(menuStore)?.menuKey,
                id: record.id,
                priceIndex: priceIndex,
                name: record.name,
                ref: (record.media || []).filter(x => x.type === 'photo')[0]?.ref,
                img: (record.media || []).filter(x => x.type === 'photo')[0]?.name,
                count: 1,
                price: price.price,
                weight: price.weight,
                mods,
                modsDescription
            });
        }
        else {
            items[found].count++;
        }

        reachGoal('ADD_TO_CART');

        return items;
    });

    eventBus.set([{event: 'addToCart', payload: {record, price, priceIndex, modificators: mods}}]);
}

export const removeFromCart = (record, price, priceIndex, mods = []) => {

    cartItemsStore.update(items => {

        const modsDescription = getModsDescription(mods);
        items = (items || []).filter(i => findItemPredicate(i, record, priceIndex, modsDescription));

        if (items.length === 0) {
            saveCart(items);
        }

        return items;
    });

    eventBus.set([{event: 'removeFromCart', payload: {record, price, priceIndex, modificators: mods}}]);
}

export const incrementItemInCart = (record, price, priceIndex, mods = []) => {

    cartItemsStore.update(items => {

        const modsDescription = getModsDescription(mods);

        (items || []).forEach(i => {
            if (findItemPredicate(i, record, priceIndex, modsDescription)) {
                i.count += 1;
                updateOrderItemData({record, priceIndex});
            }
        })

        return items;
    });

    eventBus.set([{event: 'incrementItemInCart', payload: {record, price, priceIndex, modificators: mods}}]);
}

export const decrementItemInCart = (record, price, priceIndex, mods = []) => {

    cartItemsStore.update(items => {

        const modsDescription = getModsDescription(mods);
        let idxToRemove = null;

        (items || []).forEach( (i,idx) => {

            if (findItemPredicate(i, record, priceIndex, modsDescription)) {
                i.count -= 1;
                if (i.count === 0) {
                    idxToRemove = idx;
                    closeOrderItem();
                }
                else {
                    updateOrderItemData({record, priceIndex});
                }
            }
        });

        if (idxToRemove !== null) {
            items.splice(idxToRemove, 1);
        }

        if (items.length === 0) {
            saveCart(items);
            eventBus.set([{event: 'closeEditDetailsHalfScreen', payload: {}}]);
        }

        return items;
    });

    eventBus.set([{event: 'decrementItemInCart', payload: {record, price, priceIndex, modificators: mods}}]);
}

export const loadCart = () => {

    const cartItemsStr = window.localStorage.getItem(getCartKey());
    try {
        const cartItems = JSON.parse(cartItemsStr);
        cartItemsStore.set(cartItems || []);
    }
    catch (e) {
        console.error('Load cart error', e);
    }
}

export const clearCart = () => {
    cartItemsStore.set([]);
    saveCart([]);
}

const saveCart = (items) => {
    window.localStorage.setItem(getCartKey(), JSON.stringify(items));
}

let _cachedCartKey = undefined;
const getCartKey = () => {

    if (_cachedCartKey === undefined) {
        const cartId = getMenuId( get(publishConfigStore) );
        _cachedCartKey = `dm_cart_${ cartId }`;
    }

    return _cachedCartKey;
}

export const confirmOrder = (cartItems) => {

    ordersStore.update(orders => {

        const orderData = [];
        const total = {};
        cartItems.forEach(item => {
            total.sum += (item.price * item.count);
            total.count += item.count;
            orderData.push([item.id, item.priceIndex, item.count].join('-'));
        });

        cartInfoStore.update(cartInfo => {
            cartInfo.orderData = btoa(orderData.join('!'));
            return cartInfo;
        });


        orders.push({
            date: Date.now(),
            cartItems,
            totalSum: total.sum,
            totalCount: total.count
        });

        return orders;
    })
}
